import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Zap, TrendingUp, Star } from "lucide-react";
import { converterCategories, calculators, textTools } from "../data/tools";
import { AdSlot } from "../components/AdSlot";
import { SectionLabel } from "../components/SectionLabel";
import { usePageMeta } from "../lib/usePageMeta";
import { getTrending } from "../lib/api";

const popularConversions = [
  { c: "length", f: "centimeters", t: "inches" },
  { c: "length", f: "kilometers", t: "miles" },
  { c: "weight", f: "kilograms", t: "pounds" },
  { c: "temperature", f: "celsius", t: "fahrenheit" },
  { c: "length", f: "feet", t: "meters" },
  { c: "data", f: "megabytes", t: "gigabytes" },
];

const buildIndex = () => {
  const items = [];
  converterCategories.forEach((cat) => {
    items.push({ label: `${cat.label} Converter`, path: `/converters/${cat.id}`, kind: "Converter" });
  });
  calculators.forEach((c) => items.push({ label: c.title, path: `/calculators/${c.slug}`, kind: "Calculator" }));
  textTools.forEach((c) => items.push({ label: c.title, path: `/text-tools/${c.slug}`, kind: "Text Tool" }));
  return items;
};

export default function Home() {
  usePageMeta(
    "Utiliverse — Free Online Converters, Calculators & Text Tools",
    "A fast, free universe of online utility tools: unit converters, calculators and text tools. No sign-up required."
  );
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [trending, setTrending] = useState([]);
  const index = useMemo(buildIndex, []);

  useEffect(() => {
    getTrending(6).then(setTrending);
  }, []);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return index.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 6);
  }, [query, index]);

  return (
    <div>
      {/* HERO */}
      <section className="bp-grid border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="rise">
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] bg-ink text-paper px-3 py-1.5 mb-6">
              <Zap className="w-3.5 h-3.5 text-gold" /> {converterCategories.length} converter sets · {calculators.length + textTools.length}+ tools
            </span>
            <h1 className="font-heading font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-4xl">
              Every tool you<br />need, in one<br /><span className="text-signal">universe.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-ink/70 max-w-2xl font-medium">
              Instant unit converters, calculators and text utilities. Free, ad-supported, no sign-up — just answers.
            </p>
          </div>

          {/* SEARCH */}
          <div className="mt-10 max-w-2xl relative rise" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center border-2 border-ink bg-paper shadow-brutal focus-within:shadow-signal transition-shadow">
              <Search className="w-5 h-5 ml-4 text-ink/50" />
              <input
                data-testid="home-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools — e.g. BMI, cm to inches, word counter…"
                className="flex-1 bg-transparent px-3 py-4 font-mono text-base outline-none placeholder:text-ink/40"
              />
            </div>
            {results.length > 0 && (
              <div data-testid="search-results" className="absolute z-20 left-0 right-0 mt-2 bg-paper border-2 border-ink shadow-brutal">
                {results.map((r) => (
                  <button
                    key={r.path}
                    data-testid={`search-result-${r.path}`}
                    onClick={() => navigate(r.path)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-canvas border-b border-ink/10 last:border-0 text-left"
                  >
                    <span className="font-bold">{r.label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{r.kind}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* popular chips */}
          <div className="mt-7 flex flex-wrap gap-2 rise" style={{ animationDelay: "0.2s" }}>
            <span className="font-mono text-[11px] uppercase tracking-widest text-ink/50 py-2">Popular:</span>
            {popularConversions.map((p) => (
              <Link
                key={`${p.c}-${p.f}-${p.t}`}
                to={`/convert/${p.c}/${p.f}/${p.t}`}
                data-testid={`popular-${p.f}-${p.t}`}
                className="font-mono text-xs border-2 border-ink bg-paper px-3 py-2 hover:bg-gold hover:-translate-y-0.5 transition-all"
              >
                {p.f.replace(/-/g, " ")} → {p.t.replace(/-/g, " ")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <AdSlot format="leaderboard" testid="home-ad-top" />

        {/* CONVERTERS */}
        <section className="mt-16">
          <SectionLabel n="01">Unit Converters</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t-2 border-l-2 border-ink">
            {converterCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={cat.id}
                  to={`/converters/${cat.id}`}
                  data-testid={`category-card-${cat.id}`}
                  className="group border-b-2 border-r-2 border-ink bg-paper p-6 hover:bg-canvas transition-colors relative"
                >
                  <span className="w-11 h-11 border-2 border-ink bg-canvas flex items-center justify-center mb-4 group-hover:bg-signal group-hover:text-paper transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <h3 className="font-heading font-bold text-xl tracking-tight">{cat.label}</h3>
                  <p className="text-sm text-ink/60 mt-1 leading-snug">{cat.blurb}</p>
                  <ArrowRight className="w-5 h-5 absolute top-6 right-6 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </Link>
              );
            })}
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-10 mt-16">
          {/* CALCULATORS */}
          <section className="lg:col-span-2">
            <SectionLabel n="02">Calculators & Text Tools</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink">
              {[...calculators, ...textTools].map((t) => {
                const Icon = t.icon;
                const base = calculators.includes(t) ? "calculators" : "text-tools";
                return (
                  <Link
                    key={t.slug}
                    to={`/${base}/${t.slug}`}
                    data-testid={`tool-card-${t.slug}`}
                    className="group border-b-2 border-r-2 border-ink bg-paper p-6 hover:bg-canvas transition-colors flex gap-4 items-start"
                  >
                    <span className="w-10 h-10 shrink-0 border-2 border-ink bg-canvas flex items-center justify-center group-hover:bg-klein group-hover:text-paper transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </span>
                    <div>
                      <h3 className="font-heading font-bold text-lg tracking-tight">{t.title}</h3>
                      <p className="text-sm text-ink/60 leading-snug">{t.blurb}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* SIDEBAR: trending + ad */}
          <aside className="space-y-8">
            <div>
              <SectionLabel n="03">Trending Now</SectionLabel>
              <div className="border-2 border-ink bg-paper">
                {trending.length === 0 && (
                  <div className="p-5 font-mono text-xs text-ink/50">
                    No data yet — be the first to use a tool. Trending updates live.
                  </div>
                )}
                {trending.map((t, i) => (
                  <Link
                    key={t.slug}
                    to={t.path}
                    data-testid={`trending-${t.slug}`}
                    className="flex items-center gap-3 px-4 py-3 border-b border-ink/10 last:border-0 hover:bg-canvas"
                  >
                    <span className="font-mono text-sm font-bold text-signal w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1 font-bold text-sm truncate">{t.title}</span>
                    <span className="font-mono text-[10px] text-ink/40 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {t.count}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <AdSlot format="rectangle" testid="home-ad-sidebar" />
            <div className="border-2 border-ink bg-gold p-5">
              <Star className="w-6 h-6 mb-2" />
              <p className="font-heading font-bold text-lg leading-tight">100% free & private</p>
              <p className="text-sm text-ink/70 mt-1">Everything runs in your browser. Your data never leaves your device.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
